import { splitBrandRefs } from '@shared/brand-filter';
import type { ProductsQueryParams } from '@/lib/products-api';
import { urlContentExistence } from '@/lib/content-existence-filter';
import { TABLE_PAGING_URL } from '@/lib/table-paging';
import { urlColsMask, urlSort } from '@/lib/url-state/codecs';
import type { UrlValues } from '@/lib/url-state/url-schema';
import { PRODUCTS_URL, productsStockRange } from '../products-url';

/** Columns the list sorts by (their DataTable ids). */
export const BULK_EDIT_SORT_COLUMNS = [
  'product_title',
  'product_type',
  'unitprice',
  'quantityonhand',
  'vendor_name',
  'status',
  'xero_status',
  'variant_count',
] as const;

/**
 * /products-bulk-edit in the URL: the same filters and keys as /products (`products-url.ts`,
 * reading the same old keys), plus the content filters. Until T-196 the keys were `query`,
 * `vendors` (brand names), `status`, `xeroStatus` and `sortField`/`sortDirection`; they're still
 * read (decision 30 c). `cols` comes only from a link or a saved view.
 */
export const PRODUCTS_BULK_EDIT_URL = {
  q: PRODUCTS_URL.q,
  vendorIds: PRODUCTS_URL.vendorIds,
  productTypes: PRODUCTS_URL.productTypes,
  productStatuses: PRODUCTS_URL.productStatuses,
  shopifyStatuses: PRODUCTS_URL.shopifyStatuses,
  xeroStatuses: PRODUCTS_URL.xeroStatuses,
  minStock: PRODUCTS_URL.minStock,
  maxStock: PRODUCTS_URL.maxStock,
  seoTitle: urlContentExistence('seoTitle'),
  seoDescription: urlContentExistence('seoDescription'),
  teaser: urlContentExistence('teaser'),
  sort: urlSort({
    columns: BULK_EDIT_SORT_COLUMNS,
    default: { id: 'product_title', dir: 'asc' },
    legacy: { field: 'sortField', direction: 'sortDirection' },
  }),
  cols: urlColsMask(),
  ...TABLE_PAGING_URL,
};

export type ProductsBulkEditView = UrlValues<typeof PRODUCTS_BULK_EDIT_URL>;

/** Remembered per team: the sidebar brings back the last filters and sort. */
export const PRODUCTS_BULK_EDIT_VIEW_ID = 'products.bulkEdit';

/** The list request for a view, by product (every page; the caller adds `page` and `limit`). */
export function productsBulkEditListQuery(
  view: ProductsBulkEditView,
  stock: { min: number; max: number } = productsStockRange(view)
): ProductsQueryParams {
  const brands = splitBrandRefs(view.vendorIds);
  const list = <T>(values: T[]) => (values.length > 0 ? values : undefined);
  return {
    query: view.q || undefined,
    vendorIds: list(brands.ids),
    vendors: list(brands.names),
    productTypes: list(view.productTypes),
    status: list(view.productStatuses),
    shopifyStatuses: list(view.shopifyStatuses),
    xeroStatus: list(view.xeroStatuses),
    minStock: stock.min,
    maxStock: stock.max,
    sortField: view.sort.id,
    sortDirection: view.sort.dir === 'desc' ? 'descending' : 'ascending',
    groupBy: 'product',
    seoTitle: view.seoTitle ?? undefined,
    seoDescription: view.seoDescription ?? undefined,
    teaser: view.teaser ?? undefined,
  };
}
